/**
 * AI Workspace Personal Mode Limit Message
 *
 * Shows a message when Personal mode auto-limits large database schema export.
 * Explains that only the first 100 objects per type will be exported,
 * with a note about MCP integration for full schema access.
 */

import { AlertTriangle, Sparkles, Plug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SchemaExportCounts } from '@/types/terminal';

/** Maximum objects per type for Personal mode */
const PERSONAL_MODE_MAX_OBJECTS_PER_TYPE = 100;

type AiWorkspacePersonalLimitMessageProps = {
  databaseName: string;
  counts: SchemaExportCounts;
  onContinue: () => void;
  onUpgrade: () => void;
  onCancel: () => void;
};

/**
 * Get description of what will be exported with the limit
 */
function getLimitDescription(counts: SchemaExportCounts): string {
  const parts: string[] = [];
  const max = PERSONAL_MODE_MAX_OBJECTS_PER_TYPE;

  if (counts.tableCount > 0) {
    const exported = Math.min(counts.tableCount, max);
    parts.push(`${exported} tables`);
  }
  if (counts.viewCount > 0) {
    const exported = Math.min(counts.viewCount, max);
    parts.push(`${exported} views`);
  }
  if (counts.procedureCount > 0) {
    const exported = Math.min(counts.procedureCount, max);
    parts.push(`${exported} procedures`);
  }
  if (counts.functionCount > 0) {
    const exported = Math.min(counts.functionCount, max);
    parts.push(`${exported} functions`);
  }

  if (parts.length === 0) return 'No objects will be exported.';
  if (parts.length === 1) return `Will export ${parts[0]}.`;
  if (parts.length === 2) return `Will export ${parts[0]} and ${parts[1]}.`;

  const last = parts.pop();
  return `Will export ${parts.join(', ')}, and ${last}.`;
}

/**
 * Message shown when a large database is detected in Personal mode.
 * Explains the auto-limit behavior and offers upgrade option.
 */
export function AiWorkspacePersonalLimitMessage({
  databaseName,
  counts,
  onContinue,
  onUpgrade,
  onCancel,
}: AiWorkspacePersonalLimitMessageProps) {
  const totalObjects =
    counts.tableCount + counts.viewCount + counts.procedureCount + counts.functionCount;
  const description = getLimitDescription(counts);

  return (
    <div
      className="flex flex-col items-center justify-center h-full p-8"
      data-testid="ai-workspace-personal-limit"
    >
      <div className="max-w-lg text-center space-y-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
            <AlertTriangle className="h-10 w-10 text-yellow-600 dark:text-yellow-500" />
          </div>
        </div>

        {/* Title */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Large Database Detected</h2>
          <p className="text-muted-foreground">
            The database <strong>{databaseName}</strong> contains{' '}
            <strong>{totalObjects.toLocaleString()}</strong> objects.
          </p>
        </div>

        {/* Auto-limit explanation */}
        <div className="p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm text-foreground mb-2">
            Personal mode will export up to {PERSONAL_MODE_MAX_OBJECTS_PER_TYPE} objects per type
            for the AI context files.
          </p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        {/* MCP note */}
        <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 text-left">
          <Plug className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Note:</strong> Full schema information is always available to AI agents on-demand
            through the MCP integration. Your AI tools can query any table, view, or procedure
            regardless of this limit.
          </p>
        </div>

        {/* Upgrade prompt */}
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Upgrade to Pro</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Pick and choose which objects to include in the AI context for better control
            over large schemas.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-3">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onContinue}>
            Continue with Limit
          </Button>
          <Button onClick={onUpgrade} className="gap-2">
            <Sparkles className="h-4 w-4" />
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </div>
  );
}
